import "three/examples/js/math/ConvexHull";
import "three/examples/js/geometries/ConvexGeometry";
import {v3} from 'oc-threejs/short3';

/**
 * Kopiert von ConvexBufferGeometry und erweitert um uv-Mapping
 */
export default class ConvexUvBufferGeometry extends THREE.BufferGeometry {

    /**
     * @param inputVertices die Punktwolke
     * @param basis für die UV Berechnung verwendete Basis
     */
    constructor(inputVertices, basis = new THREE.Matrix4()) {
        super();
        this.basis = basis;

        // buffers
        const vertices = [];
        const normals = [];
        const uvs = [];

        const xAxis = v3();
        const yAxis = v3();
        const zAxis = v3();
        basis.extractBasis(xAxis, yAxis, zAxis);
        const inverseBrettBasis = new THREE.Matrix4().copy(basis).invert();
        const uvBoundingBox = new THREE.Box3().setFromPoints(inputVertices.map(v => v.clone().applyMatrix4(inverseBrettBasis)));

        // UV-Verlauf der Texturen in der Ebene zur jeweiligen Brett-Basis-Normalen
        const planeBasisUV_X = ['y', 'z'];
        const planeBasisUV_Y = ['z', 'x'];
        const planeBasisUV_Z = ['y', 'x'];

        new THREE.ConvexHull().setFromPoints(inputVertices).faces.forEach(face => {
            let edge = face.edge;

            // Ermittlung der planeBasisUV anhand der am besten übereinstimmenden Basis-Achse
            const xAxisDot = Math.abs(face.normal.dot(xAxis));
            const yAxisDot = Math.abs(face.normal.dot(yAxis));
            const zAxisDot = Math.abs(face.normal.dot(zAxis));
            let planeBasisUV;
            if (yAxisDot < xAxisDot) {
                if (zAxisDot < xAxisDot) {
                    planeBasisUV = planeBasisUV_X;
                } else {
                    planeBasisUV = planeBasisUV_Z;
                }
            } else {
                if (zAxisDot < yAxisDot) {
                    planeBasisUV = planeBasisUV_Y;
                } else {
                    planeBasisUV = planeBasisUV_Z;
                }
            }

            // we move along a doubly-connected edge list to access all face points (see HalfEdge
            // docs)
            do {
                const point = edge.head().point;
                vertices.push(point.x, point.y, point.z);
                normals.push(face.normal.x, face.normal.y, face.normal.z);

                const pointUV = point.clone().applyMatrix4(inverseBrettBasis);
                const uv = pointUV.sub(uvBoundingBox.min);
                uvs.push(uv[planeBasisUV[0]], uv[planeBasisUV[1]]);

                edge = edge.next;
            } while (edge !== face.edge);
        });

        // build geometry
        this.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        this.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        this.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    }

    /**
     * Convenience-Methode, um eine Box zu erzeugen. Alle Parameter tragen default-Werte und können
     * weg gelassen werden. Im Gegensatz zu einer THREE.BoxGeometry sind die UV-Koordinaten
     * entsprechend der Size angepasst.
     */
    static createBox(width, height, depth, widthSegments, heightSegments, depthSegments) {
        return new ConvexUvBufferGeometry(new THREE.BoxGeometry(width, height, depth, widthSegments, heightSegments, depthSegments).vertices);
    }

    /**
     * Convenience-Methode, um eine Sphere zu erzeugen. Alle Parameter tragen default-Werte und
     * können weg gelassen werden. Im Gegensatz zu einer THREE.SphereGeometry sind die
     * UV-Koordinaten entsprechend der Size angepasst.
     */
    static createSphere(radius, widthSegments, heightSegments, phiStart, phiLength, thetaStart, thetaLength) {
        return new ConvexUvBufferGeometry(new THREE.SphereGeometry(radius, widthSegments, heightSegments, phiStart, phiLength, thetaStart, thetaLength).vertices);
    }


    /**
     * Rundet eine beliebige Geometry ab, d.h. alle Ecken und Kanten werden abgerundet, und gibt
     * eine neue Geometry zurück.
     *
     * @param geometry
     * @param bevelDistance Breite der Abrundung
     * @param bevelSteps wie oft wird intern gebevelt, größere Werte ergeben rundere Kanten, bei 1
     *     einfach unterteilt = eine 45° abgeschrägte Kante, bei 2 wird diese wiederum in zwei
     *     Teilkanten unterteilt usw.
     * @param basis für die UV-Berechnung verwendete Koordinatenbasis
     * @return ConvexUvBufferGeometry
     */
    static bevelGeometry(geometry, bevelDistance = 0.01, bevelSteps = 2, basis = new THREE.Matrix4()) {

        const bevel = (geometry, bevelFactor) => {
            const vertices = [];
            const _0 = v3();
            const _1 = v3();
            const addBeveledPoint = (a, b, c, n) => {
                _0.subVectors(b, a).normalize().multiplyScalar(bevelFactor);
                _1.subVectors(c, a).normalize().multiplyScalar(bevelFactor);
                vertices.push(a.clone().add(_0).add(_1));
            };
            geometry.faces.forEach(f => {
                const a = geometry.vertices[f.a];
                const b = geometry.vertices[f.b];
                const c = geometry.vertices[f.c];
                addBeveledPoint(a, b, c, f.normal);
                addBeveledPoint(b, c, a, f.normal);
                addBeveledPoint(c, a, b, f.normal);
            });
            return new ConvexUvBufferGeometry(vertices, basis);
        }

        for (let i = 0; i < bevelSteps; i++) {
            if (geometry instanceof THREE.BufferGeometry) {
                geometry = new THREE.Geometry().fromBufferGeometry(geometry);
            }
            geometry = bevel(geometry, bevelDistance / bevelSteps);
        }

        return geometry;

    }

}

